import i18next from 'i18next';

import {escapeHtml} from './html';

const MOBILE_CATEGORY_MODAL_QUERY = '(max-width: 767px)';

const t = (key, defaultValue, options = {}) => (
    i18next.t ? i18next.t(key, {defaultValue, ...options}) : defaultValue
);

const normalizeCategoryId = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    return String(value);
};

const normalizeParentCategoryId = (value) => {
    const normalized = normalizeCategoryId(value);
    return normalized === '0' ? '' : normalized;
};

const getCategoryId = (item) => normalizeCategoryId(item?.id ?? item?.category_id ?? item?.value ?? item?.code);

const getCategoryName = (item, id) => String(
    item?.name
    || item?.label
    || item?.title
    || item?.display_name
    || id
    || ''
);

export const normalizeCategoryPickerCategories = (items, parentId = '') => {
    const categories = [];
    (items || []).forEach((item) => {
        if (!item || typeof item !== 'object') {
            return;
        }
        const id = getCategoryId(item);
        if (!id) {
            return;
        }
        const explicitParentId = normalizeParentCategoryId(item.parent_id ?? item.parentId ?? item.parent);
        const resolvedParentId = explicitParentId || normalizeParentCategoryId(parentId);
        categories.push({
            id,
            name: getCategoryName(item, id),
            parent_id: resolvedParentId || null,
            raw: item,
        });
        const children = item.children || item.subcategories || item.categories || [];
        if (Array.isArray(children) && children.length) {
            categories.push(...normalizeCategoryPickerCategories(children, id));
        }
    });
    return categories;
};

const sortCategories = (categories) => [...categories].sort((left, right) => (
    String(left.name || '').localeCompare(String(right.name || ''))
));

export const createOlxCategoryPicker = ({
    id,
    categories = [],
    selectedCategoryId = '',
    onSelect = () => {},
} = {}) => {
    const pickerId = id || `olx-category-picker-${Math.random().toString(36).slice(2)}`;
    const state = {
        categories: normalizeCategoryPickerCategories(categories),
        categoriesById: new Map(),
        selectedCategoryPath: [],
        modalCategorySearch: '',
        mobileCategoryLevel: 0,
        disabled: false,
    };

    const rebuildIndex = () => {
        state.categoriesById = new Map(state.categories.map((category) => [normalizeCategoryId(category.id), category]));
    };

    const getChildrenCategories = (parentId) => sortCategories(
        state.categories.filter((category) => normalizeCategoryId(category.parent_id) === normalizeCategoryId(parentId)),
    );

    const buildSelectedCategoryPath = (categoryId) => {
        const normalizedId = normalizeCategoryId(categoryId);
        if (!normalizedId) {
            return [];
        }
        const path = [];
        let current = state.categoriesById.get(normalizedId);
        while (current) {
            path.unshift(current.id);
            current = current.parent_id ? state.categoriesById.get(normalizeCategoryId(current.parent_id)) : null;
        }
        return path;
    };

    const getLeafCategoryId = () => {
        if (!state.selectedCategoryPath.length) {
            return null;
        }
        const candidate = state.selectedCategoryPath[state.selectedCategoryPath.length - 1];
        return getChildrenCategories(candidate).length ? null : candidate;
    };

    const selectedCategoryLabel = () => {
        if (!state.selectedCategoryPath.length) {
            return t('ads:draftEditor.selectCategory', 'Select category');
        }
        return state.selectedCategoryPath.map((categoryId) => {
            const category = state.categoriesById.get(normalizeCategoryId(categoryId));
            return category ? category.name : categoryId;
        }).join(' / ');
    };

    const categoryBreadcrumbHtml = () => {
        if (!state.selectedCategoryPath.length) {
            return `<span>${escapeHtml(t('ads:draftEditor.categoryPathEmpty', 'No category selected'))}</span>`;
        }
        return escapeHtml(selectedCategoryLabel());
    };

    const isMobileCategoryModal = () => window.matchMedia(MOBILE_CATEGORY_MODAL_QUERY).matches;

    const filterCategoriesBySearch = (categories) => {
        const query = String(state.modalCategorySearch || '').trim().toLowerCase();
        if (!query) {
            return categories;
        }
        return categories.filter((category) => String(category.name || '').toLowerCase().includes(query));
    };

    const categoryColumnOptionHtml = (category, levelIndex, selectedId) => {
        const hasChildren = getChildrenCategories(category.id).length > 0;
        const isSelected = normalizeCategoryId(category.id) === normalizeCategoryId(selectedId);
        return `
            <button
                type="button"
                class="draft-category-option ${isSelected ? 'is-selected' : ''}"
                data-level-index="${escapeHtml(levelIndex)}"
                data-category-id="${escapeHtml(category.id)}"
                data-has-children="${hasChildren ? '1' : '0'}"
            >
                <span class="draft-category-option__label">${escapeHtml(category.name)}</span>
                <span class="draft-category-option__arrow ${hasChildren ? '' : 'is-hidden'}">&#8250;</span>
            </button>
        `;
    };

    const topCategoryCardsHtml = () => {
        const roots = filterCategoriesBySearch(getChildrenCategories(null));
        if (!roots.length) {
            return `<div class="workspace-empty">${escapeHtml(t('ads:draftEditor.noCategoriesFound', 'No matching categories found.'))}</div>`;
        }
        return `
            <div class="draft-category-cards-grid">
                ${roots.map((category, index) => `
                    <button type="button" class="draft-category-card theme-${index % 4}" data-root-category-id="${escapeHtml(category.id)}">
                        <span class="draft-category-card__badge">${escapeHtml(String(category.name || '?').charAt(0))}</span>
                        <span class="draft-category-card__label">${escapeHtml(category.name)}</span>
                    </button>
                `).join('')}
            </div>
        `;
    };

    const categoryColumnHtml = (parentId, levelIndex) => {
        const categoriesForColumn = getChildrenCategories(parentId);
        if (!categoriesForColumn.length) {
            return '';
        }
        const selectedId = state.selectedCategoryPath[levelIndex] || null;
        return `
            <div class="draft-category-column" data-level-index="${escapeHtml(levelIndex)}">
                ${categoriesForColumn.map((category) => categoryColumnOptionHtml(category, levelIndex, selectedId)).join('')}
            </div>
        `;
    };

    const categoryColumnsHtml = () => {
        const columns = [];
        let parentId = null;
        let levelIndex = 0;
        while (true) {
            const column = categoryColumnHtml(parentId, levelIndex);
            if (!column) {
                break;
            }
            columns.push(column);
            const selectedId = state.selectedCategoryPath[levelIndex];
            if (!selectedId) {
                break;
            }
            parentId = selectedId;
            levelIndex += 1;
        }
        return `<div class="draft-category-columns">${columns.join('')}</div>`;
    };

    const scrollCategoryColumnsToLevel = (levelIndex) => {
        if (isMobileCategoryModal()) {
            return;
        }
        window.requestAnimationFrame(() => {
            const columnsWrap = document.querySelector(`#${pickerId}-modal-body .draft-category-columns`);
            if (!columnsWrap) {
                return;
            }
            const targetColumn = columnsWrap.querySelector(`.draft-category-column[data-level-index="${levelIndex}"]`);
            if (!targetColumn) {
                return;
            }
            const padding = 12;
            const visibleLeft = columnsWrap.scrollLeft;
            const visibleRight = visibleLeft + columnsWrap.clientWidth;
            const columnLeft = targetColumn.offsetLeft;
            const columnRight = columnLeft + targetColumn.offsetWidth;

            if (columnRight + padding > visibleRight) {
                columnsWrap.scrollLeft = Math.max(0, columnRight - columnsWrap.clientWidth + padding);
            } else if (columnLeft - padding < visibleLeft) {
                columnsWrap.scrollLeft = Math.max(0, columnLeft - padding);
            }
        });
    };

    const mobileCategoryLevelHtml = () => {
        const levelIndex = Number(state.mobileCategoryLevel || 0);
        const parentId = state.selectedCategoryPath[levelIndex - 1] || null;
        if (!parentId) {
            return topCategoryCardsHtml();
        }
        const selectedId = state.selectedCategoryPath[levelIndex] || null;
        const categoriesForLevel = filterCategoriesBySearch(getChildrenCategories(parentId));
        if (!categoriesForLevel.length) {
            return `<div class="workspace-empty">${escapeHtml(t('ads:draftEditor.noCategoriesFound', 'No matching categories found.'))}</div>`;
        }
        return `
            <div class="draft-category-mobile-toolbar">
                <button type="button" class="draft-category-mobile-back">
                    <span class="draft-category-mobile-back__arrow">&#8249;</span>
                    <span>${escapeHtml(t('ads:draftEditor.back', 'Back'))}</span>
                </button>
                <div class="draft-category-breadcrumb draft-category-breadcrumb--mobile">${categoryBreadcrumbHtml()}</div>
            </div>
            <div class="draft-category-mobile-list">
                ${categoriesForLevel.map((category) => categoryColumnOptionHtml(category, levelIndex, selectedId)).join('')}
            </div>
        `;
    };

    const categoryModalBodyMobileHtml = () => {
        const showRootLevel = !state.selectedCategoryPath.length || Number(state.mobileCategoryLevel || 0) === 0;
        if (showRootLevel) {
            return `
                <div class="draft-category-modal__search-row">
                    <input
                        type="text"
                        id="${escapeHtml(pickerId)}-search"
                        class="draft-input"
                        placeholder="${escapeHtml(t('ads:draftEditor.categorySearch', 'Search category'))}"
                        value="${escapeHtml(state.modalCategorySearch)}"
                    >
                </div>
                ${topCategoryCardsHtml()}
            `;
        }
        return `
            <div class="draft-category-modal__search-row">
                <input
                    type="text"
                    id="${escapeHtml(pickerId)}-search"
                    class="draft-input"
                    placeholder="${escapeHtml(t('ads:draftEditor.categorySearchCurrentLevel', 'Search current level'))}"
                    value="${escapeHtml(state.modalCategorySearch)}"
                >
            </div>
            ${mobileCategoryLevelHtml()}
        `;
    };

    const categoryModalBodyHtml = () => {
        if (isMobileCategoryModal()) {
            return categoryModalBodyMobileHtml();
        }
        if (!state.selectedCategoryPath.length) {
            return `
                <div class="draft-category-modal__search-row">
                    <input
                        type="text"
                        id="${escapeHtml(pickerId)}-search"
                        class="draft-input"
                        placeholder="${escapeHtml(t('ads:draftEditor.categorySearch', 'Search category'))}"
                        value="${escapeHtml(state.modalCategorySearch)}"
                    >
                </div>
                ${topCategoryCardsHtml()}
            `;
        }
        return `
            <div class="draft-category-modal__toolbar">
                <button type="button" class="outline-button draft-category-reset">
                    ${escapeHtml(t('ads:draftEditor.changeTopCategory', 'Choose another top category'))}
                </button>
                <div class="draft-category-breadcrumb">${categoryBreadcrumbHtml()}</div>
            </div>
            ${categoryColumnsHtml()}
        `;
    };

    const renderTrigger = () => `
        <button type="button" id="${escapeHtml(pickerId)}-trigger" class="draft-category-trigger" ${state.disabled ? 'disabled' : ''}>
            <span class="draft-category-trigger__label">${escapeHtml(selectedCategoryLabel())}</span>
            <span class="draft-category-trigger__arrow">&#8250;</span>
        </button>
    `;

    const renderModal = () => `
        <div id="${escapeHtml(pickerId)}-modal" class="draft-category-modal-overlay d-none">
            <div class="draft-category-modal-dialog">
                <div class="draft-category-modal-header">
                    <h3>${escapeHtml(t('ads:draftEditor.chooseCategory', 'Choose category'))}</h3>
                    <button type="button" class="btn-close draft-category-modal-close" aria-label="${escapeHtml(t('ads:draftEditor.closeCategoryModal', 'Close'))}"></button>
                </div>
                <div id="${escapeHtml(pickerId)}-modal-body" class="draft-category-modal-body">
                    ${categoryModalBodyHtml()}
                </div>
            </div>
        </div>
    `;

    const renderField = ({label = '', disabled = false} = {}) => {
        state.disabled = Boolean(disabled);
        return `
            ${label ? `<label class="draft-field-label" for="${escapeHtml(pickerId)}-trigger">${escapeHtml(label)}</label>` : ''}
            <div id="${escapeHtml(pickerId)}-shell" class="draft-category-selector-shell">
                ${renderTrigger()}
            </div>
            ${renderModal()}
        `;
    };

    const renderTriggerIntoDom = () => {
        $(`#${pickerId}-shell`).html(renderTrigger());
    };

    const renderModalBodyIntoDom = () => {
        $(`#${pickerId}-modal-body`).html(categoryModalBodyHtml());
    };

    const open = () => {
        if (state.disabled) {
            return;
        }
        state.modalCategorySearch = '';
        state.mobileCategoryLevel = state.selectedCategoryPath.length > 1
            ? state.selectedCategoryPath.length - 1
            : 0;
        $(`#${pickerId}-modal`).removeClass('d-none');
        renderModalBodyIntoDom();
    };

    const close = () => {
        $(`#${pickerId}-modal`).addClass('d-none');
    };

    const selectCategoryPathItem = (categoryId, levelIndex, hasChildren) => {
        state.selectedCategoryPath = state.selectedCategoryPath.slice(0, levelIndex);
        state.selectedCategoryPath[levelIndex] = normalizeCategoryId(categoryId);
        state.modalCategorySearch = '';
        if (isMobileCategoryModal() && hasChildren) {
            state.mobileCategoryLevel = levelIndex + 1;
        }
        renderTriggerIntoDom();
        renderModalBodyIntoDom();
        if (hasChildren) {
            scrollCategoryColumnsToLevel(levelIndex + 1);
        }
        const leafId = getLeafCategoryId();
        if (leafId) {
            const category = state.categoriesById.get(normalizeCategoryId(leafId));
            onSelect(leafId, {
                category,
                path: state.selectedCategoryPath
                    .map((itemId) => state.categoriesById.get(normalizeCategoryId(itemId)))
                    .filter(Boolean),
            });
            close();
        }
    };

    const bind = (root = document) => {
        const namespace = `.olxCategoryPicker${pickerId.replace(/[^a-zA-Z0-9]/g, '')}`;
        const target = $(root);
        target.off(namespace);
        target.on(`click${namespace}`, `#${pickerId}-trigger`, open);
        target.on(`click${namespace}`, `#${pickerId}-modal .draft-category-modal-close`, close);
        target.on(`click${namespace}`, `#${pickerId}-modal`, (event) => {
            if (event.target.id === `${pickerId}-modal`) {
                close();
            }
        });
        target.on(`input${namespace}`, `#${pickerId}-search`, (event) => {
            state.modalCategorySearch = String($(event.currentTarget).val() || '').trim();
            renderModalBodyIntoDom();
        });
        target.on(`click${namespace}`, `#${pickerId}-modal .draft-category-card`, (event) => {
            const categoryId = normalizeCategoryId($(event.currentTarget).data('root-category-id'));
            if (!categoryId) {
                return;
            }
            const hasChildren = getChildrenCategories(categoryId).length > 0;
            if (isMobileCategoryModal() && hasChildren) {
                state.mobileCategoryLevel = 1;
            }
            selectCategoryPathItem(categoryId, 0, hasChildren);
        });
        target.on(`click${namespace}`, `#${pickerId}-modal .draft-category-option`, (event) => {
            const option = $(event.currentTarget);
            const levelIndex = Number(option.data('level-index'));
            const categoryId = normalizeCategoryId(option.data('category-id'));
            const hasChildren = Number(option.data('has-children')) === 1;
            if (!categoryId) {
                return;
            }
            selectCategoryPathItem(categoryId, levelIndex, hasChildren);
        });
        target.on(`click${namespace}`, `#${pickerId}-modal .draft-category-mobile-back`, () => {
            if (!isMobileCategoryModal()) {
                return;
            }
            const currentLevel = Number(state.mobileCategoryLevel || 0);
            state.mobileCategoryLevel = currentLevel > 1 ? currentLevel - 1 : 0;
            state.modalCategorySearch = '';
            renderModalBodyIntoDom();
        });
        target.on(`click${namespace}`, `#${pickerId}-modal .draft-category-reset`, () => {
            state.selectedCategoryPath = [];
            state.modalCategorySearch = '';
            state.mobileCategoryLevel = 0;
            renderTriggerIntoDom();
            renderModalBodyIntoDom();
            onSelect('', {category: null, path: []});
        });
    };

    const setCategories = (nextCategories) => {
        state.categories = normalizeCategoryPickerCategories(nextCategories);
        rebuildIndex();
        state.selectedCategoryPath = buildSelectedCategoryPath(getLeafCategoryId() || state.selectedCategoryPath[state.selectedCategoryPath.length - 1]);
    };

    const setSelectedCategoryId = (categoryId) => {
        state.selectedCategoryPath = buildSelectedCategoryPath(categoryId);
        state.modalCategorySearch = '';
        state.mobileCategoryLevel = state.selectedCategoryPath.length > 1 ? state.selectedCategoryPath.length - 1 : 0;
    };

    rebuildIndex();
    setSelectedCategoryId(selectedCategoryId);

    return {
        bind,
        close,
        open,
        renderField,
        setCategories,
        setSelectedCategoryId,
        getSelectedCategoryLabel: selectedCategoryLabel,
    };
};
